import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Minus, Plus, X, MessageCircle, ArrowLeft, Truck, CreditCard, Banknote, User, Phone, MapPin } from "lucide-react";
import { formatPKR } from "../lib/format";

const DELIVERY_FEE = 350;
const FREE_DELIVERY_THRESHOLD = 5000;

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, clear } = useCart();
  const { config } = useAuth();

  const [showCheckout, setShowCheckout] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    paymentMethod: "Cash on Delivery",
  });

  const deliveryFee = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const total = subtotal + deliveryFee;

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Please enter your name.";
    if (!formData.phone.trim()) newErrors.phone = "Please enter your phone number.";
    else if (!/^[0-9+\s\-]{7,}$/.test(formData.phone.trim())) newErrors.phone = "Enter a valid phone number.";
    if (!formData.address.trim()) newErrors.address = "Please enter your delivery address.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildWhatsAppMessage = () => {
    const lines = [];
    lines.push("*New Order — MZ Scents*");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push("");
    lines.push("*🛍️ Order Items:*");
    items.forEach((it, i) => {
      lines.push(`${i + 1}. ${it.name} (${it.brand}) — ${it.volume_ml}ml`);
      lines.push(`   Qty: ${it.qty} × ${formatPKR(it.price)} = ${formatPKR(it.price * it.qty)}`);
    });
    lines.push("");
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push(`*Subtotal:* ${formatPKR(subtotal)}`);
    lines.push(`*Delivery:* ${deliveryFee === 0 ? "FREE 🎉" : formatPKR(deliveryFee)}`);
    lines.push(`*Total:* ${formatPKR(total)}`);
    lines.push("━━━━━━━━━━━━━━━━━━━━");
    lines.push("");
    lines.push("*📦 Customer Details:*");
    lines.push(`*Name:* ${formData.name}`);
    lines.push(`*Phone:* ${formData.phone}`);
    lines.push(`*Address:* ${formData.address}`);
    lines.push(`*Payment:* ${formData.paymentMethod}`);
    lines.push("");
    lines.push("Thank you for shopping with MZ Scents! ✨");
    return lines.join("\n");
  };

  const handleConfirmOrder = () => {
    if (!validate()) return;
    const msg = encodeURIComponent(buildWhatsAppMessage());
    const number = (config?.whatsapp_number || "923180465911").replace(/\D/g, "");
    const url = `https://wa.me/${number}?text=${msg}`;
    window.open(url, "_blank", "noopener,noreferrer");
    clear();
    setOpen(false);
    setShowCheckout(false);
    setFormData({ name: "", phone: "", address: "", paymentMethod: "Cash on Delivery" });
  };

  const handleClose = (val) => {
    setOpen(val);
    if (!val) {
      setShowCheckout(false);
      setErrors({});
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-[440px] bg-cream border-l border-gold-soft p-0 flex flex-col"
      >
        {/* ─── CART VIEW ─── */}
        {!showCheckout && (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-gold-soft/60">
              <SheetTitle className="font-serif text-3xl text-ink text-left">
                Your Cart
              </SheetTitle>
              <div className="eyebrow text-left text-[10px]">
                {items.length} {items.length === 1 ? "item" : "items"}
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div
                  data-testid="cart-empty"
                  className="h-full flex flex-col items-center justify-center text-center gap-3 py-20"
                >
                  <div className="eyebrow text-ash">Nothing here yet</div>
                  <p className="font-serif text-2xl text-ink max-w-xs">
                    Curate your shelf — start with something rare.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gold-soft/60">
                  {items.map((it) => (
                    <li
                      key={it.id}
                      data-testid={`cart-item-${it.id}`}
                      className="py-5 flex gap-4"
                    >
                      <div className="w-20 h-24 bg-sand overflow-hidden flex-shrink-0">
                        <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex flex-col">
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <div className="eyebrow text-[9px] text-gold/90">{it.brand}</div>
                            <div className="font-serif text-lg text-ink leading-tight">
                              {it.name}
                            </div>
                            <div className="text-xs text-ash mt-0.5">{it.volume_ml}ml</div>
                          </div>
                          <button
                            data-testid={`cart-remove-${it.id}`}
                            onClick={() => removeItem(it.id)}
                            className="text-ash hover:text-ink transition"
                            aria-label="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-auto pt-3">
                          <div className="flex items-center border border-gold-soft">
                            <button
                              data-testid={`cart-dec-${it.id}`}
                              onClick={() => updateQty(it.id, it.qty - 1)}
                              className="px-2.5 py-1.5 hover:bg-sand transition"
                              aria-label="Decrease"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="px-3 text-sm tabular-nums min-w-[2ch] text-center">
                              {it.qty}
                            </span>
                            <button
                              data-testid={`cart-inc-${it.id}`}
                              onClick={() => updateQty(it.id, it.qty + 1)}
                              className="px-2.5 py-1.5 hover:bg-sand transition"
                              aria-label="Increase"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <div className="text-sm font-medium text-ink tabular-nums">
                            {formatPKR(it.price * it.qty)}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-gold-soft/60 p-6 space-y-4">
                {/* Subtotal & delivery preview */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="eyebrow">Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-sans text-xl font-medium text-ink tracking-wide">
                      {formatPKR(subtotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1.5 text-ash">
                      <Truck size={12} />
                      Delivery
                    </span>
                    {deliveryFee === 0 ? (
                      <span className="text-gold font-medium tracking-wide">FREE 🎉</span>
                    ) : (
                      <span className="text-ash">{formatPKR(deliveryFee)}</span>
                    )}
                  </div>
                  {deliveryFee > 0 && (
                    <p className="text-[10px] text-ash/80 leading-relaxed">
                      Add {formatPKR(FREE_DELIVERY_THRESHOLD - subtotal)} more for free delivery!
                    </p>
                  )}
                  <div className="flex items-center justify-between border-t border-gold-soft/40 pt-2 mt-1">
                    <span className="eyebrow text-ink">Total</span>
                    <span className="font-sans text-lg font-semibold text-ink tracking-wide">
                      {formatPKR(total)}
                    </span>
                  </div>
                </div>

                <button
                  data-testid="proceed-checkout-btn"
                  onClick={() => setShowCheckout(true)}
                  className="w-full bg-ink text-cream py-4 flex items-center justify-center gap-2.5 text-sm font-medium tracking-wider uppercase hover:bg-gold transition-colors"
                >
                  Proceed to Checkout
                </button>
                <button
                  data-testid="clear-cart-btn"
                  onClick={clear}
                  className="w-full text-[11px] text-ash hover:text-ink tracking-widest uppercase transition"
                >
                  Clear cart
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── CHECKOUT FORM VIEW ─── */}
        {showCheckout && (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-gold-soft/60">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setShowCheckout(false); setErrors({}); }}
                  className="text-ash hover:text-ink transition -ml-1"
                  aria-label="Back to cart"
                >
                  <ArrowLeft size={18} />
                </button>
                <SheetTitle className="font-serif text-2xl text-ink text-left">
                  Checkout
                </SheetTitle>
              </div>
              <div className="eyebrow text-left text-[10px] mt-1">
                Fill in your details to place your order
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Customer Info */}
              <div className="space-y-4">
                <div className="eyebrow text-[10px] mb-1">Your Information</div>

                {/* Name */}
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-xs text-ash uppercase tracking-widest">
                    <User size={11} /> Name
                  </label>
                  <input
                    id="checkout-name"
                    type="text"
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    className={`w-full bg-white border ${errors.name ? "border-red-400" : "border-gold-soft"} px-3.5 py-2.5 text-sm text-ink placeholder:text-ash/60 focus:outline-none focus:border-gold transition`}
                  />
                  {errors.name && <p className="text-red-500 text-[10px]">{errors.name}</p>}
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-xs text-ash uppercase tracking-widest">
                    <Phone size={11} /> Phone Number
                  </label>
                  <input
                    id="checkout-phone"
                    type="tel"
                    placeholder="+92 300 0000000"
                    value={formData.phone}
                    onChange={(e) => handleFieldChange("phone", e.target.value)}
                    className={`w-full bg-white border ${errors.phone ? "border-red-400" : "border-gold-soft"} px-3.5 py-2.5 text-sm text-ink placeholder:text-ash/60 focus:outline-none focus:border-gold transition`}
                  />
                  {errors.phone && <p className="text-red-500 text-[10px]">{errors.phone}</p>}
                </div>

                {/* Address */}
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-xs text-ash uppercase tracking-widest">
                    <MapPin size={11} /> Delivery Address
                  </label>
                  <textarea
                    id="checkout-address"
                    placeholder="House No., Street, City"
                    rows={3}
                    value={formData.address}
                    onChange={(e) => handleFieldChange("address", e.target.value)}
                    className={`w-full bg-white border ${errors.address ? "border-red-400" : "border-gold-soft"} px-3.5 py-2.5 text-sm text-ink placeholder:text-ash/60 focus:outline-none focus:border-gold transition resize-none`}
                  />
                  {errors.address && <p className="text-red-500 text-[10px]">{errors.address}</p>}
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <div className="eyebrow text-[10px] mb-1">Payment Method</div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: "cod", label: "Cash on Delivery", icon: Banknote, value: "Cash on Delivery" },
                    { id: "online", label: "Transfer Online", icon: CreditCard, value: "Transfer Money Online" },
                  ].map(({ id, label, icon: Icon, value }) => (
                    <button
                      key={id}
                      id={`payment-${id}`}
                      onClick={() => handleFieldChange("paymentMethod", value)}
                      className={`flex flex-col items-center gap-2 p-4 border-2 transition-all text-center ${
                        formData.paymentMethod === value
                          ? "border-gold bg-gold/10 text-ink"
                          : "border-gold-soft/60 text-ash hover:border-gold/50"
                      }`}
                    >
                      <Icon size={20} strokeWidth={1.5} className={formData.paymentMethod === value ? "text-gold" : "text-ash"} />
                      <span className="text-[10px] uppercase tracking-wider font-medium leading-tight">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-sand/50 border border-gold-soft/50 p-4 space-y-2.5">
                <div className="eyebrow text-[10px] mb-2">Order Summary</div>
                {items.map((it) => (
                  <div key={it.id} className="flex justify-between text-xs text-ink/80">
                    <span>{it.name} × {it.qty}</span>
                    <span className="tabular-nums">{formatPKR(it.price * it.qty)}</span>
                  </div>
                ))}
                <div className="border-t border-gold-soft/40 pt-2 mt-2 space-y-1.5">
                  <div className="flex justify-between text-xs text-ash">
                    <span>Subtotal</span>
                    <span className="tabular-nums">{formatPKR(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="flex items-center gap-1 text-ash"><Truck size={10}/> Delivery</span>
                    {deliveryFee === 0 ? (
                      <span className="text-gold font-medium">FREE 🎉</span>
                    ) : (
                      <span className="text-ash tabular-nums">{formatPKR(deliveryFee)}</span>
                    )}
                  </div>
                  <div className="flex justify-between text-sm font-semibold text-ink border-t border-gold-soft/40 pt-1.5 mt-1">
                    <span>Total</span>
                    <span className="tabular-nums">{formatPKR(total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirm Button */}
            <div className="border-t border-gold-soft/60 p-6 space-y-3">
              <button
                data-testid="whatsapp-checkout-btn"
                onClick={handleConfirmOrder}
                className="w-full bg-whatsapp text-white py-4 flex items-center justify-center gap-2.5 text-sm font-medium tracking-wider uppercase hover:bg-whatsapp-hover transition-colors"
              >
                <MessageCircle size={16} strokeWidth={2} />
                Confirm Order via WhatsApp
              </button>
              <button
                onClick={() => { setShowCheckout(false); setErrors({}); }}
                className="w-full text-[11px] text-ash hover:text-ink tracking-widest uppercase transition"
              >
                Back to Cart
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
